import React, { useState, useEffect, useContext, useRef } from "react";
import { jwtDecode } from "jwt-decode";
import { UserContext } from "../utils/userContext";
import { useT } from "../contexts/TranslationContext";
import { useFormik } from "formik";
import * as Yup from "yup";
import axiosInstance from "../utils/axiosInstance";
import PageHeader from "../components/ui/PageHeader";
import PageWrapper from "../components/ui/PageWrapper";
import Button from "../components/ui/Button";
import { useBackNavigation } from "../navigation/BackNavigationContext";
import * as Sentry from "@sentry/react";
import TaskTimeEntryList from "../components/TaskTimeEntry/TaskTimeEntryList";
import TaskPickerModal from "../components/TaskTimeEntry/TaskPickerModal";
import ReportFlagChips from "../components/ReportFlags/ReportFlagChips";
import ReportFlagSheet from "../components/ReportFlags/ReportFlagSheet";
import InternalNoteSheet from "../components/ReportFlags/InternalNoteSheet";
import {
  FLAG_TYPES,
  createDraft,
  serialiseDrafts,
  appendDraftPhotos,
} from "../components/ReportFlags/reportFlagDrafts";
import { getRecentTasks, addRecentTask } from "../utils/recentTasksStorage";
import { getProjectLastViewed } from "../utils/projectViewTracking";
import { resizeImageFiles, ALLOWED_PHOTO_TYPES } from "../utils/imageResize";
import { usePhotoUpload } from "../contexts/PhotoUploadContext";
import {
  formatPhotoHours,
  reportPhotoRequirement,
} from "../utils/reportPhotoRequirement";

/** Sentinel for native `<select>` "show all projects"; must never match a real project id. */
const SHOW_ALL_PROJECTS_VALUE = "__show_all_projects__";

function Report({ prefilledDate, toggleView, reportToEdit = null, prefilledWorker = null }) {
  const [photos, setPhotos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [isProcessingPhotos, setIsProcessingPhotos] = useState(false);
  const [isAbsent, setIsAbsent] = useState(reportToEdit?.absence?.isAbsent || false);
  const [absenceReason, setAbsenceReason] = useState(reportToEdit?.absence?.absenceReason || "");
  const [flexDayRepeatCount, setFlexDayRepeatCount] = useState(0);
  const [holidayBackToWorkDate, setHolidayBackToWorkDate] = useState("");
  const [otherDescription, setOtherDescription] = useState(reportToEdit?.absence?.absenceReason === "Other" ? reportToEdit?.description || "" : "");
  const descriptionRef = useRef(null);
  const hoursRef = useRef(null);
  const otherDescriptionRef = useRef(null);
  
  // Task-based time entry state
  const [taskBundle, setTaskBundle] = useState(null);
  const [taskBundleLoading, setTaskBundleLoading] = useState(false);
  const [taskEntries, setTaskEntries] = useState([]);
  const [isTaskPickerOpen, setIsTaskPickerOpen] = useState(false);
  const [recentTasks, setRecentTasks] = useState([]);
  const [descriptionTranslateOpen, setDescriptionTranslateOpen] = useState(false);
  const [descriptionTranslationLoading, setDescriptionTranslationLoading] = useState(false);
  const [descriptionTranslation, setDescriptionTranslation] = useState(null);
  const [descriptionTranslationLang, setDescriptionTranslationLang] = useState(null);
  const descriptionTranslateRef = useRef(null);

  // Deviation / ÄTA / missing task drafts, submitted with the report itself
  const [flagDrafts, setFlagDrafts] = useState([]);
  const [flagSheet, setFlagSheet] = useState(null); // { type, initialDraft }
  const [flagsSentMessage, setFlagsSentMessage] = useState("");
  const [internalNote, setInternalNote] = useState(reportToEdit?.internalNote || "");
  const [internalNoteSheetOpen, setInternalNoteSheetOpen] = useState(false);

  const today = new Date().toISOString().split("T")[0];
  const { user } = useContext(UserContext);
  const { t } = useT();
  const { enqueuePhotos } = usePhotoUpload();
  const { markAsRoot, setPageFallback } = useBackNavigation();

  useEffect(() => {
    // Treat report as a root entry point
    markAsRoot(true);
    // By default, time reports view should fallback to the report page
    setPageFallback("/report");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset flex day repeat count when absence reason changes from Flex Day
  useEffect(() => {
    if (absenceReason !== "Flex day") {
      setFlexDayRepeatCount(0);
    }
  }, [absenceReason]);

  // Reset holiday back to work date when absence reason changes from Holiday
  useEffect(() => {
    if (absenceReason !== "Holiday") {
      setHolidayBackToWorkDate("");
    }
  }, [absenceReason]);

  // Reset other description when absence reason changes from Other
  useEffect(() => {
    if (absenceReason !== "Other") {
      setOtherDescription("");
    }
  }, [absenceReason]);

  // Focus other description textarea when Other is selected
  useEffect(() => {
    if (absenceReason === "Other" && otherDescriptionRef.current) {
      setTimeout(() => {
        otherDescriptionRef.current?.focus();
      }, 0);
    }
  }, [absenceReason]);

  // Helper function to calculate same weekday for next N weeks
  const getNextWeeksSameDay = (startDate, weeks) => {
    if (weeks <= 0) return [];
    
    const dates = [];
    const start = new Date(startDate);
    const dayOfWeek = start.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    // Calculate dates for the same weekday in the next N weeks
    for (let i = 1; i <= weeks; i++) {
      const nextDate = new Date(start);
      nextDate.setDate(start.getDate() + (i * 7)); // Add i weeks
      dates.push(nextDate);
    }
    
    return dates;
  };

  // Helper function to calculate all dates between start and end (inclusive of start, exclusive of end)
  const getHolidayDateRange = (startDate, endDate) => {
    const dates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    // Validate: end must be after start
    if (end <= start) return [];
    
    // Generate all dates from start (inclusive) to end (exclusive)
    const current = new Date(start);
    while (current < end) {
      dates.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    
    return dates;
  };

  const allowedPhotoTypes = ALLOWED_PHOTO_TYPES;

  const [projectsListShowsAll, setProjectsListShowsAll] = useState(false);
  const [projectsLoadingAll, setProjectsLoadingAll] = useState(false);

  // User data is now automatically populated by UserContext from JWT token
  // No need to manually fetch or decode

  const reportEditKey = reportToEdit?._id || reportToEdit?.id || "";

  useEffect(() => {
    async function fetchProjects() {
      try {
        // Request projects from backend and ask it to only return the desired statuses.
        // Send as a comma-separated string to be robust across different axios/express parsers.
        const response = await axiosInstance.get("/projects/list", {
          params: { statuses: "in_progress,upcoming", page: 1, limit: 500 },
        });
        // API returns a paginated object { items, total, ... }
        setProjects(response.data.items || []);
      } catch (error) {
        console.error("Error fetching projects:", error);
      }
    }

    setProjectsListShowsAll(false);
    fetchProjects();
  }, [reportEditKey]);

  useEffect(() => {
    setInternalNote(reportToEdit?.internalNote || "");
  }, [reportEditKey, reportToEdit?.internalNote]);

  // State to track selected project for task bundle fetching
  const [selectedProjectId, setSelectedProjectId] = useState(reportToEdit?.projectId || "");
  const [selectedProjectShowTasks, setSelectedProjectShowTasks] = useState(false);

  // Fetch task bundle only when project has task selection enabled (avoids unnecessary requests/translations)
  useEffect(() => {
    if (!selectedProjectId || isAbsent) {
      setTaskBundle(null);
      setTaskEntries([]);
      setTaskBundleLoading(false);
      setSelectedProjectShowTasks(false);
      return;
    }

    const selectedProject = projects.find(p => p._id === selectedProjectId);

    // Don't fetch or assume until we know the project's setting (projects loaded and project found)
    if (!projects.length) {
      setTaskBundle(null);
      setTaskBundleLoading(false);
      setSelectedProjectShowTasks(false);
      return;
    }
    if (!selectedProject) {
      setTaskBundle(null);
      setTaskBundleLoading(false);
      setSelectedProjectShowTasks(false);
      return;
    }

    const showTasks = selectedProject.showTasksInTimeReport !== false;
    setSelectedProjectShowTasks(showTasks);

    if (!showTasks) {
      setTaskBundle(null);
      setTaskEntries([]);
      setTaskBundleLoading(false);
      return;
    }

    let cancelled = false;
    setTaskBundleLoading(true);
    setTaskEntries([]);

    async function fetchTaskBundle(projectId) {
      try {
        const response = await axiosInstance.get(`/task-bundles/projects/${projectId}/task-bundle`);
        if (cancelled) return;
        // Kept even when it holds no tasks: the section still renders, and an admin can
        // add the project's first task straight from the picker.
        setTaskBundle(response.data || null);
      } catch (error) {
        if (cancelled) return;
        if (error.response?.status !== 404) {
          console.error("Error fetching task bundle:", error);
        }
        setTaskBundle(null);
      } finally {
        if (!cancelled) setTaskBundleLoading(false);
      }
    }

    fetchTaskBundle(selectedProjectId);
    return () => { cancelled = true; setTaskBundleLoading(false); };
  }, [selectedProjectId, isAbsent, projects]);

  // Load recent tasks when user is available
  useEffect(() => {
    if (user?.userId) {
      setRecentTasks(getRecentTasks(user.userId));
    }
  }, [user?.userId]);

  // Load task entries when editing a report with taskEntries
  useEffect(() => {
    // No bundle is not a reason to drop the rows: a missing-task row exists before its task does.
    if (reportToEdit?.taskEntries && reportToEdit.taskEntries.length > 0 && !taskBundleLoading) {
      // Map task entries to the format expected by the component
      const loadedEntries = reportToEdit.taskEntries.map(entry => {
        // A missing-task row has no task in the bundle yet; keep it as a pending row.
        if (!entry.taskId) {
          return {
            pendingFlagId: entry.pendingFlagId,
            isPending: true,
            hours: String(entry.hours || ""),
            description: entry.description || "",
            title: entry.title || "",
            titleDisplay: entry.title || "",
            displayName: entry.title || "",
            type: "task",
          };
        }

        // Find the task/subtask in the bundle to get title, showDescriptionInTimeReport, and hours info
        const task = (taskBundle?.tasks || []).find(t => t._id.toString() === entry.taskId.toString());
        let title = entry.title || "Task";
        let titleDisplay = title;
        let taskTitle;
        let taskTitleDisplay;
        let showDescriptionInTimeReport = false;
        let requireDescriptionInTimeReport = false;
        let estimatedHours = null;
        let trackedHours = 0;
        let subtask = null;
        
        if (entry.subtaskId && task) {
          subtask = task.subtasks.find(st => st._id.toString() === entry.subtaskId.toString());
          if (subtask) {
            title = subtask.title;
            titleDisplay = subtask.titleDisplay ?? subtask.title ?? title;
            taskTitle = task.title;
            taskTitleDisplay = task.titleDisplay ?? task.title;
            showDescriptionInTimeReport = subtask.showDescriptionInTimeReport || false;
            requireDescriptionInTimeReport = subtask.requireDescriptionInTimeReport || false;
            estimatedHours = subtask.estimatedHours;
            trackedHours = subtask.trackedHours || 0;
          }
        } else if (task) {
          title = task.title;
          titleDisplay = task.titleDisplay ?? task.title ?? title;
          showDescriptionInTimeReport = task.showDescriptionInTimeReport || false;
          requireDescriptionInTimeReport = task.requireDescriptionInTimeReport || false;
          estimatedHours = task.estimatedHours;
          trackedHours = task.trackedHours || 0;
        }
        
        return {
          bundleId: entry.bundleId,
          taskId: entry.taskId,
          subtaskId: entry.subtaskId || undefined,
          hours: String(entry.hours || ""),
          description: entry.description || "",
          title,
          titleDisplay,
          displayName: title,
          titleLanguage: entry.titleLanguage || (entry.subtaskId && subtask ? (subtask.titleLanguage || task?.titleLanguage) : task?.titleLanguage),
          showDescriptionInTimeReport,
          requireDescriptionInTimeReport,
          type: entry.subtaskId ? "subtask" : "task",
          billingModel: task?.billingModel || null,
          taskTitle,
          taskTitleDisplay,
          estimatedHours,
          trackedHours,
        };
      });
      setTaskEntries(loadedEntries);
    } else if (reportToEdit && !reportToEdit.taskEntries && reportToEdit.linkedTask) {
      // Legacy format - convert to taskEntries format for editing
      if (taskBundle) {
        const task = taskBundle.tasks.find(t => t._id.toString() === reportToEdit.linkedTask.taskId.toString());
        if (task) {
          let entry = {
            bundleId: reportToEdit.linkedTask.bundleId,
            taskId: reportToEdit.linkedTask.taskId,
            hours: String(reportToEdit.hours || ""),
            description: reportToEdit.description || "",
            title: task.title,
            titleDisplay: task.titleDisplay ?? task.title,
            displayName: task.title,
            titleLanguage: task.titleLanguage,
            showDescriptionInTimeReport: task.showDescriptionInTimeReport || false,
            requireDescriptionInTimeReport: task.requireDescriptionInTimeReport || false,
            type: "task",
            billingModel: task.billingModel || null, // Include billing model from task
            estimatedHours: task.estimatedHours,
            trackedHours: task.trackedHours || 0,
          };
          
          if (reportToEdit.linkedTask.subtaskId) {
            const subtask = task.subtasks.find(st => st._id.toString() === reportToEdit.linkedTask.subtaskId.toString());
            if (subtask) {
              entry.subtaskId = subtask._id;
              entry.title = subtask.title;
              entry.titleDisplay = subtask.titleDisplay ?? subtask.title;
              entry.displayName = `${task.title} > ${subtask.title}`;
              entry.titleLanguage = subtask.titleLanguage || task.titleLanguage;
              entry.showDescriptionInTimeReport = subtask.showDescriptionInTimeReport || false;
              entry.requireDescriptionInTimeReport = subtask.requireDescriptionInTimeReport || false;
              entry.type = "subtask";
              entry.taskTitle = task.title;
              entry.taskTitleDisplay = task.titleDisplay ?? task.title;
              // Use subtask billing when set, otherwise inherit from parent task
              entry.billingModel = subtask.billingModel || task.billingModel || null;
              entry.estimatedHours = subtask.estimatedHours;
              entry.trackedHours = subtask.trackedHours || 0;
            }
          }
          
          setTaskEntries([entry]);
        }
      }
    }
  }, [reportToEdit, taskBundle, taskBundleLoading]);

  // Task entry management functions
  const handleAddTaskEntry = (taskItem) => {
    // Use functional update to handle multiple additions in sequence
    setTaskEntries(prev => {
      // Check if already added
      // Normalize subtaskId for comparison (undefined and null should be treated the same)
      const normalizeSubtaskId = (id) => id === null || id === undefined ? null : id;
      const exists = prev.some(
        e => e.taskId === taskItem.taskId && normalizeSubtaskId(e.subtaskId) === normalizeSubtaskId(taskItem.subtaskId)
      );
      if (exists) return prev;
      return [...prev, { ...taskItem, hours: "", description: "" }];
    });
    
    // Save to recent tasks
    if (user?.userId) {
      addRecentTask(user.userId, taskItem);
      setRecentTasks(getRecentTasks(user.userId));
    }
  };

  const handleUpdateTaskHours = (idx, hours) => {
    const updated = [...taskEntries];
    updated[idx] = { ...updated[idx], hours };
    setTaskEntries(updated);
  };

  const handleUpdateTaskDescription = (idx, description) => {
    const updated = [...taskEntries];
    updated[idx] = { ...updated[idx], description };
    setTaskEntries(updated);
  };

  const handleRemoveTaskEntry = (idx) => {
    setTaskEntries(taskEntries.filter((_, i) => i !== idx));
  };

  // A missing-task draft shows as a normal row so its hours count toward the day's total
  // straight away, rather than the worker's report looking short until an admin acts.
  const pendingDraftRows = flagDrafts
    .filter((draft) => draft.type === FLAG_TYPES.MISSING_TASK)
    .map((draft) => ({
      localDraftId: draft.localId,
      isPending: true,
      title: draft.proposedTaskName,
      displayName: draft.proposedTaskName,
      hours: draft.hours,
      description: "",
      type: "task",
    }));

  const combinedEntries = [...taskEntries, ...pendingDraftRows];

  const projectHasTasks = !!taskBundle?.tasks?.length;
  // Task rows replace the plain hours field once there is something to report against.
  // A project set up for task reporting that has no tasks yet keeps both, so the worker
  // can either flag the missing task or just log hours instead of being blocked.
  const taskEntryMode =
    selectedProjectShowTasks && (projectHasTasks || combinedEntries.length > 0);
  const showHoursField = !taskEntryMode && !(selectedProjectShowTasks && taskBundleLoading);

  const currentPhotoRequirement = (hoursValue) => {
    if (reportToEdit) {
      return { requiredCount: 0, photoRequiredHours: 0, allTasksOptedOut: false };
    }
    return reportPhotoRequirement({
      isAbsent,
      entries: taskEntryMode ? combinedEntries : null,
      tasks: taskBundle?.tasks || [],
      reportHours: hoursValue,
    });
  };

  const handleCombinedUpdateHours = (idx, hours) => {
    if (idx < taskEntries.length) {
      handleUpdateTaskHours(idx, hours);
      return;
    }
    const draftId = pendingDraftRows[idx - taskEntries.length]?.localDraftId;
    setFlagDrafts((prev) =>
      prev.map((draft) => (draft.localId === draftId ? { ...draft, hours } : draft))
    );
  };

  const handleCombinedUpdateDescription = (idx, description) => {
    if (idx < taskEntries.length) handleUpdateTaskDescription(idx, description);
  };

  const handleCombinedRemove = (idx) => {
    if (idx < taskEntries.length) {
      handleRemoveTaskEntry(idx);
      return;
    }
    const draftId = pendingDraftRows[idx - taskEntries.length]?.localDraftId;
    setFlagDrafts((prev) => prev.filter((draft) => draft.localId !== draftId));
  };

  const handleSaveFlagDraft = (draft) => {
    setFlagDrafts((prev) => {
      const exists = prev.some((d) => d.localId === draft.localId);
      return exists ? prev.map((d) => (d.localId === draft.localId ? draft : d)) : [...prev, draft];
    });
  };

  const handleRemoveFlagDraft = (localId) => {
    setFlagDrafts((prev) => prev.filter((draft) => draft.localId !== localId));
  };

  /** Opened from the task picker when a worker searched for a task that does not exist. */
  const handleReportMissingTask = (proposedTaskName) => {
    setIsTaskPickerOpen(false);
    setFlagSheet({
      type: FLAG_TYPES.MISSING_TASK,
      initialDraft: createDraft(FLAG_TYPES.MISSING_TASK, { proposedTaskName }),
    });
  };

  useEffect(() => {
    if (!descriptionTranslateOpen) return;
    const handleClickOutside = (e) => {
      if (descriptionTranslateRef.current && !descriptionTranslateRef.current.contains(e.target)) {
        setDescriptionTranslateOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [descriptionTranslateOpen]);

  const handleDescriptionTranslateClick = async () => {
    if (descriptionTranslateOpen) {
      setDescriptionTranslateOpen(false);
      return;
    }
    const text = (formik.values.description || "").trim();
    if (!text) {
      setDescriptionTranslation(null);
      setDescriptionTranslationLang(null);
      setDescriptionTranslateOpen(true);
      return;
    }
    setDescriptionTranslateOpen(true);
    setDescriptionTranslationLoading(true);
    setDescriptionTranslation(null);
    setDescriptionTranslationLang(null);
    try {
      const body = { text };
      if (reportToEdit?.descriptionLanguage) {
        body.sourceLanguage = reportToEdit.descriptionLanguage;
      }
      const res = await axiosInstance.post("/reports/translate-description", body);
      setDescriptionTranslation(res.data.translatedText ?? "");
      setDescriptionTranslationLang(res.data.targetLanguage ?? null);
    } catch (err) {
      console.error("Translation failed:", err);
      setDescriptionTranslation(null);
    } finally {
      setDescriptionTranslationLoading(false);
    }
  };

  useEffect(() => {
    if (isProcessingPhotos) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
  }, [isProcessingPhotos]);

  const formik = useFormik({
    initialValues: {
      hours: reportToEdit ? String(reportToEdit.hours) : "",
      description: reportToEdit?.description || "",
      date: reportToEdit?.date?.slice(0, 10) || prefilledDate || today,
      worker: reportToEdit?.userId || prefilledWorker || "",
      project: reportToEdit?.projectId || "",
    },
    validationSchema: Yup.object().shape({
      hours: isAbsent || taskEntryMode
        ? Yup.string()
        : Yup.string()
            .required(t("report.validationRequired"))
            .transform((value) => value.replace(",", "."))
            .test(
              "is-valid-number",
              t("report.validationHoursFormat"),
              (value) => {
                if (!value) return false;
                const normalizedValue = parseFloat(value);
                return (
                  !isNaN(normalizedValue) &&
                  /^\d+(\.\d{1})?$/.test(normalizedValue.toString())
                );
              }
            )
            .test("is-positive", t("report.validationHoursPositive"), (value) => {
              const normalizedValue = parseFloat(value);
              return !isNaN(normalizedValue) && normalizedValue > 0;
            }),
      description: isAbsent ? Yup.string() : Yup.string().required(t("report.validationRequired")),
      date: Yup.date().required(t("report.validationRequired")),
      project: isAbsent ? Yup.string() : Yup.string().required(t("report.validationRequired")),
    }),
    enableReinitialize: true,
    onSubmit: async (values) => {
      if (isProcessingPhotos) {
        alert(t("report.validationWaitPhotos"));
        return;
      }

      // Validate absence reason if absent
      if (isAbsent && !absenceReason) {
        alert(t("report.validationSelectAbsenceReason"));
        return;
      }

      // Validate other description if Other is selected (mandatory field)
      if (isAbsent && absenceReason === "Other") {
        if (!otherDescription || !otherDescription.trim()) {
          alert(t("report.validationOtherDescriptionRequired"));
          if (otherDescriptionRef.current) {
            otherDescriptionRef.current.focus();
          }
          return;
        }
      }

      // Validate holiday back to work date if Holiday is selected
      if (isAbsent && absenceReason === "Holiday" && holidayBackToWorkDate) {
        const reportDate = new Date(values.date);
        const backToWorkDate = new Date(holidayBackToWorkDate);
        if (backToWorkDate <= reportDate) {
          alert(t("report.validationBackToWorkAfter"));
          return;
        }
      }

      // Validate task entries if project has tasks
      if (!isAbsent && taskEntryMode && combinedEntries.length === 0) {
        alert(t("report.validationAddTaskEntry"));
        return;
      }

      // Validate each task entry has hours
      if (!isAbsent && taskEntryMode && combinedEntries.length > 0) {
        const invalidEntry = combinedEntries.find(e => {
          const hours = parseFloat(String(e.hours).replace(",", "."));
          return isNaN(hours) || hours <= 0;
        });
        if (invalidEntry) {
          alert(t("report.validationTaskEntryHours"));
          return;
        }
      }

      const photoRequirement = currentPhotoRequirement(values.hours);
      if (photos.length < photoRequirement.requiredCount) {
        alert(
          t("report.validationPhotosRequired", {
            count: photoRequirement.requiredCount,
          })
        );
        return;
      }

      // Validate required descriptions for task entries
      if (!isAbsent && taskEntryMode && taskEntries.length > 0) {
        const missingDescription = taskEntries.find(e => 
          e.requireDescriptionInTimeReport && !(e.description || "").trim()
        );
        if (missingDescription) {
          alert(t("report.validationTaskDescriptionRequired", { taskName: missingDescription.title || missingDescription.displayName || "Task" }));
          return;
        }
      }

      const formData = new FormData();
      formData.append("date", values.date);
      formData.append("userId", values.worker || user?.userId);

      if (isAbsent) {
        // Absent mode: send absence object with isAbsent, absenceReason, and absenceReasonId
        // Find the ID for the selected absence reason
        const absenceReasonOptions = [
          { value: "Flex day", id: "F" },
          { value: "Holiday", id: "H" },
          { value: "Illness", id: "I" },
          { value: "No work available", id: "N" },
          { value: "Other", id: "O" },
        ];
        const selectedOption = absenceReasonOptions.find(
          (opt) => opt.value === absenceReason
        );
        const absenceData = {
          isAbsent: true,
          absenceReason: absenceReason,
          absenceReasonId: selectedOption?.id || "O",
        };
        formData.append("absence", JSON.stringify(absenceData));
        // For "Other", include the custom description
        if (absenceReason === "Other" && otherDescription.trim()) {
          formData.append("description", otherDescription.trim());
        }
        // Project is optional when absent
        if (values.project) {
          formData.append("projectId", values.project);
        }
      } else if (taskEntryMode && combinedEntries.length > 0) {
        // Task-based mode: send task entries array with general description
        // Draft missing-task rows travel as flags instead; the server recreates their
        // pending entries once it knows the flag ids.
        formData.append("projectId", values.project);
        formData.append("absence", JSON.stringify({ isAbsent: false }));
        formData.append("description", values.description);
        formData.append("taskEntries", JSON.stringify(taskEntries.map(e => ({
          bundleId: e.bundleId,
          taskId: e.taskId,
          // Send subtaskId only if it exists, otherwise omit it (will be undefined/null in JSON)
          // Backend will handle undefined/null correctly
          ...(e.subtaskId ? { subtaskId: e.subtaskId } : {}),
          ...(e.pendingFlagId ? { pendingFlagId: e.pendingFlagId } : {}),
          hours: parseFloat(String(e.hours).replace(",", ".")),
          description: e.description || "",
          title: e.displayName || e.title,
          ...(e.titleLanguage ? { titleLanguage: e.titleLanguage } : {}),
        }))));
      } else {
        // Normal mode: send all required fields
        const normalizedHours = parseFloat(values.hours.replace(",", "."));
        formData.append("hours", normalizedHours);
        formData.append("description", values.description);
        formData.append("projectId", values.project);
        // Send absence object with isAbsent: false
        formData.append("absence", JSON.stringify({ isAbsent: false }));
      }

      if (!isAbsent) {
        formData.append("internalNote", internalNote.trim());
      }

      if (!isAbsent && flagDrafts.length > 0) {
        formData.append("flags", JSON.stringify(serialiseDrafts(flagDrafts, taskEntries)));
        appendDraftPhotos(formData, flagDrafts);
      }

      setLoading(true);
      setSuccessMessage("");

      // Log submission data to Sentry
      const logDescription = isAbsent 
        ? (absenceReason === "Other" && otherDescription.trim() ? otherDescription.trim() : absenceReason)
        : taskEntryMode ? `${taskEntries.length} task entries` : values.description;
      
      const totalTaskHours = taskEntries.reduce((sum, e) => sum + (parseFloat(String(e.hours).replace(",", ".")) || 0), 0);
      
      Sentry.captureMessage(
        reportToEdit ? "Updating time report" : "Submitting time report",
        {
          level: "info",
          extra: {
            userId: user?.userId,
            project: values.project,
            hours: isAbsent ? 0 : taskEntryMode ? totalTaskHours : parseFloat(values.hours.replace(",", ".")),
            description: logDescription,
            date: values.date,
            isAbsent,
            hasTaskEntries: taskEntryMode && taskEntries.length > 0,
            taskEntriesCount: taskEntries.length,
            attachedPhotosCount: isAbsent ? 0 : photos.length,
          },
        }
      );

      try {
        // Find the ID for the selected absence reason (used in both Flex Day and Holiday)
        const absenceReasonOptions = [
          { value: "Flex day", id: "F" },
          { value: "Holiday", id: "H" },
          { value: "Illness", id: "I" },
          { value: "No work available", id: "N" },
          { value: "Other", id: "O" },
        ];
        const selectedOption = absenceReasonOptions.find(
          (opt) => opt.value === absenceReason
        );

        // Handle Flex Day with repeat count
        if (
          isAbsent &&
          absenceReason === "Flex day" &&
          flexDayRepeatCount > 0 &&
          !reportToEdit
        ) {
          // Create multiple absence reports for the same weekday in future weeks
          const datesToCreate = [
            values.date,
            ...getNextWeeksSameDay(values.date, flexDayRepeatCount).map((date) =>
              date.toISOString().split("T")[0]
            ),
          ];

          let successCount = 0;
          let errorCount = 0;

          // Create reports for all dates
          for (const dateStr of datesToCreate) {
            try {
              const reportFormData = new FormData();
              reportFormData.append("date", dateStr);
              reportFormData.append("userId", values.worker || user?.userId);
              // Send absence as an object
              const absenceData = {
                isAbsent: true,
                absenceReason: absenceReason,
                absenceReasonId: selectedOption?.id || "O",
              };
              reportFormData.append("absence", JSON.stringify(absenceData));
              if (values.project) {
                reportFormData.append("projectId", values.project);
              }

              await axiosInstance.post("/reports", reportFormData, {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              });
              successCount++;
            } catch (error) {
              console.error(`Error creating report for ${dateStr}:`, error);
              errorCount++;
            }
          }

          if (successCount > 0) {
            // Reset form
            formik.resetForm();
            formik.setFieldValue("date", today);
            setAbsenceReason("");
            setIsAbsent(false);
            setFlexDayRepeatCount(0);
            setHolidayBackToWorkDate("");
            setOtherDescription("");

            // Show success message
            setSuccessMessage(
              t("report.successAbsenceReports", {
                count: successCount,
                plural: successCount > 1 ? "s" : "",
                failed: errorCount > 0 ? t("report.failedCount", { count: errorCount }) : "",
              })
            );

            // Navigate to TimeReportsView at the initial date after 1 second
            setTimeout(() => {
              toggleView(values.date);
            }, 1000);
          } else {
            setSuccessMessage(t("report.failedAbsenceReports"));
            setTimeout(() => {
              setSuccessMessage("");
            }, 5000);
          }
        } else if (
          isAbsent &&
          absenceReason === "Holiday" &&
          holidayBackToWorkDate &&
          !reportToEdit
        ) {
          // Handle Holiday with date range
          const datesToCreate = getHolidayDateRange(values.date, holidayBackToWorkDate).map((date) =>
            date.toISOString().split("T")[0]
          );

          let successCount = 0;
          let errorCount = 0;

          // Create reports for all dates in the range
          for (const dateStr of datesToCreate) {
            try {
              const reportFormData = new FormData();
              reportFormData.append("date", dateStr);
              reportFormData.append("userId", values.worker || user?.userId);
              // Send absence as an object
              const absenceData = {
                isAbsent: true,
                absenceReason: absenceReason,
                absenceReasonId: selectedOption?.id || "O",
              };
              reportFormData.append("absence", JSON.stringify(absenceData));
              if (values.project) {
                reportFormData.append("projectId", values.project);
              }

              await axiosInstance.post("/reports", reportFormData, {
                headers: {
                  "Content-Type": "multipart/form-data",
                },
              });
              successCount++;
            } catch (error) {
              console.error(`Error creating report for ${dateStr}:`, error);
              errorCount++;
            }
          }

          if (successCount > 0) {
            // Reset form
            formik.resetForm();
            formik.setFieldValue("date", today);
            setAbsenceReason("");
            setIsAbsent(false);
            setHolidayBackToWorkDate("");
            setOtherDescription("");

            // Show success message
            setSuccessMessage(
              t("report.successAbsenceReports", {
                count: successCount,
                plural: successCount > 1 ? "s" : "",
                failed: errorCount > 0 ? t("report.failedCount", { count: errorCount }) : "",
              })
            );

            // Navigate to TimeReportsView at the start date after 1 second
            setTimeout(() => {
              toggleView(values.date);
            }, 1000);
          } else {
            setSuccessMessage(t("report.failedAbsenceReports"));
            setTimeout(() => {
              setSuccessMessage("");
            }, 5000);
          }
        } else {
          // Save the report first. Selected photos upload afterward so a slow
          // connection does not hold the hours. The count was already checked.
          const photosToUpload = isAbsent ? [] : [...photos];
          const endpoint = reportToEdit
            ? `/reports/${reportToEdit._id}`
            : "/reports";
          const method = reportToEdit ? "put" : "post";

          const saveResponse = await axiosInstance[method](endpoint, formData, {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          });

          const savedReportId =
            saveResponse.data?.report?._id || reportToEdit?._id;

          if (photosToUpload.length > 0 && !savedReportId) {
            Sentry.captureMessage("Report saved but id missing; photos not uploaded", {
              level: "error",
              extra: { userId: user?.userId, photoCount: photosToUpload.length },
            });
          } else if (savedReportId && photosToUpload.length > 0) {
            enqueuePhotos(savedReportId, photosToUpload);
          }

          // Reset form
          formik.resetForm();
          formik.setFieldValue("date", today);
          setPhotos([]);
          setFlexDayRepeatCount(0);
          setHolidayBackToWorkDate("");
          setOtherDescription("");
          setTaskEntries([]);
          setTaskBundle(null);
          setSelectedProjectId("");

          const fileInput = document.querySelector("#photo-upload");
          if (fileInput) fileInput.value = "";

          const successText = reportToEdit
            ? t("report.reportUpdated")
            : t("report.reportSubmitted");
          setSuccessMessage(successText);
          setFlagsSentMessage(
            flagDrafts.length > 0
              ? t("reportFlags.sentToManager", { count: flagDrafts.length })
              : ""
          );
          setFlagDrafts([]);

          // Navigate to TimeReportsView; photo uploads keep running on this tab.
          setTimeout(() => {
            toggleView(values.date);
          }, 1000);
        }

        // Log successful submission
        Sentry.captureMessage(
          reportToEdit
            ? "Time report updated successfully"
            : "Time report submitted successfully",
          {
            level: "info",
            extra: {
              userId: user?.userId,
              project: values.project,
              attachedPhotosCount: photos.length,
            },
          }
        );
      } catch (error) {
        alert(t("report.errorSubmitting"));
        console.log(error);

        // Log submission error to Sentry
        Sentry.captureException(error, {
          extra: {
            userId: user?.userId,
            project: values.project,
            attachedPhotosCount: photos.length,
          },
        });
      } finally {
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchUsers = async () => {
      if (!user?.isAdmin) return;
      try {
        const response = await axiosInstance.get("/users", {
          params: { page: 1, limit: 500 },
        });
        // Support both paginated { items } and plain array responses
        setUsers(response.data.items || response.data || []);
      } catch (error) {
        console.error("Error fetching users:", error);
      }
    };

    fetchUsers();

    // Default the worker field to prefilledWorker if provided, otherwise to the logged-in user if not set
    if (prefilledWorker && !formik.values.worker) {
      formik.setFieldValue("worker", prefilledWorker);
    } else if (user?.userId && !formik.values.worker && !prefilledWorker) {
      formik.setFieldValue("worker", user.userId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, prefilledWorker]);

  const handlePhotoUpload = async (e) => {
    const files = Array.from(e.target.files);

    const validFiles = files.filter((file) =>
      allowedPhotoTypes.includes(file.type)
    );

    if (validFiles.length !== files.length) {
      const invalidTypes = files
        .filter((file) => !allowedPhotoTypes.includes(file.type))
        .map((file) => ({ name: file.name, type: file.type }));

      Sentry.captureMessage("Some selected files were not valid image types", {
        level: "warning",
        extra: {
          invalidFiles: invalidTypes,
          userId: user?.userId,
        },
      });

      alert("Only JPG, PNG, and WEBP image files are allowed.");

      const fileInput = document.querySelector("#photo-upload");
      if (fileInput) fileInput.value = "";
      return;
    }

    const compressedPhotos = [];

    if (validFiles.length === 0) {
      Sentry.captureMessage("No files selected for upload", { level: "info" });
      return;
    }

    setIsProcessingPhotos(true);

    compressedPhotos.push(
      ...(await resizeImageFiles(validFiles, (error, file) => {
        Sentry.captureException(error, {
          extra: {
            fileName: file.name,
            errorMessage: "Error processing image upload",
          },
        });
      }))
    );

    // After processing images reset state to enable submit button
    setPhotos(compressedPhotos);
    setIsProcessingPhotos(false);

    if (compressedPhotos.length > 0) {
      Sentry.captureMessage("Photos successfully processed", {
        level: "info",
        extra: { photoCount: compressedPhotos.length },
      });
    } else {
      Sentry.captureMessage("No photos processed after compression", {
        level: "warning",
      });
    }

    setPhotos(compressedPhotos);
  };

  const photoRequirement = currentPhotoRequirement(formik.values.hours);
  const photosShort = photoRequirement.requiredCount > photos.length;

  return (
    <>
      <PageHeader
        title={t("report.title")}
        actions={
          <Button variant="outline" size="sm" onClick={toggleView}>
            {t("report.viewReports")}
          </Button>
        }
      />
      <PageWrapper>
        <form
          onSubmit={formik.handleSubmit}
          className="w-full max-w-md space-y-4"
        >
          {/* Date field with Absent toggle */}
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <label>{t("report.date")}</label>
              <div className="max-w-[260px]">
                <input
                  type="date"
                  name="date"
                  value={formik.values.date}
                  onChange={(e) => {
                    formik.handleChange(e);
                    // Focus hours field when a date is selected (only if not absent)
                    if (e.target.value && !isAbsent && hoursRef.current) {
                      setTimeout(() => {
                        hoursRef.current?.focus();
                      }, 0);
                    }
                  }}
                  onBlur={formik.handleBlur}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              {formik.touched.date && formik.errors.date ? (
                <div className="text-red-500">{formik.errors.date}</div>
              ) : null}
            </div>

            <div className="flex flex-col items-end">
              <label className="block mb-2">{t("report.absent")}</label>
              <button
                type="button"
                onClick={() => {
                  setIsAbsent(!isAbsent);
                  if (!isAbsent) {
                    setAbsenceReason("");
                  }
                }}
                className={`relative inline-flex h-8 w-16 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
                  isAbsent ? "bg-blue-600" : "bg-gray-300"
                }`}
              >
                <span
                  className={`inline-block h-6 w-6 transform rounded-full bg-white shadow-lg transition-transform ${
                    isAbsent ? "translate-x-9" : "translate-x-1"
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Conditional fields based on absent status */}
          {!isAbsent ? (
            <>
              <div>
                <label>{t("report.project")}</label>
                <select
                  name="project"
                  value={formik.values.project}
                  onChange={async (e) => {
                    const value = e.target.value;
                    if (value === SHOW_ALL_PROJECTS_VALUE) {
                      if (projectsLoadingAll) return;
                      setProjectsLoadingAll(true);
                      try {
                        const response = await axiosInstance.get("/projects/list", {
                          params: { page: 1, limit: 500 },
                        });
                        setProjects(response.data.items || []);
                        setProjectsListShowsAll(true);
                      } catch (error) {
                        console.error("Error fetching all projects:", error);
                      } finally {
                        setProjectsLoadingAll(false);
                      }
                      return;
                    }
                    formik.handleChange(e);
                    setSelectedProjectId(value);
                  }}
                  onBlur={formik.handleBlur}
                  disabled={projectsLoadingAll}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="" label={t("report.selectProject")} />
                  {[...projects]
                    .sort((a, b) => {
                      // Sort by last viewed (most recent first), then by name
                      const aLastViewed = getProjectLastViewed(a._id);
                      const bLastViewed = getProjectLastViewed(b._id);
                      
                      if (aLastViewed && bLastViewed) {
                        const timeDiff = bLastViewed.getTime() - aLastViewed.getTime();
                        if (timeDiff !== 0) return timeDiff;
                      } else if (aLastViewed && !bLastViewed) {
                        return -1;
                      } else if (!aLastViewed && bLastViewed) {
                        return 1;
                      }
                      
                      return (a.name || "").localeCompare(b.name || "");
                    })
                    .map((project) => (
                      <option key={project._id} value={project._id}>
                        {projectsListShowsAll && project.status === "done"
                          ? `${project.name} - ${project.address} (${t("report.projectStatusDone")})`
                          : `${project.name} - ${project.address}`}
                      </option>
                    ))}
                  {!projectsListShowsAll ? (
                    <option value={SHOW_ALL_PROJECTS_VALUE}>
                      {projectsLoadingAll ? t("report.loadingAllProjects") : t("report.showAllProjects")}
                    </option>
                  ) : null}
                </select>
                {formik.touched.project && formik.errors.project ? (
                  <div className="text-red-500">{formik.errors.project}</div>
                ) : null}
              </div>

              {user?.isAdmin && (
                <div>
                  <label>{t("report.worker")}</label>
                  <select
                    name="worker"
                    value={formik.values.worker}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="" label={t("report.selectWorker")} />
                    {users.map((u) => (
                      <option key={u._id} value={u._id}>
                        {`${u.firstname} ${u.lastname || ""}`}
                      </option>
                    ))}
                  </select>
                  {formik.touched.worker && formik.errors.worker ? (
                    <div className="text-red-500">{formik.errors.worker}</div>
                  ) : null}
                </div>
              )}

              {/* Task-based entry whenever the project reports on tasks, including before
                  its first task exists so the picker's missing-task route stays reachable */}
              {selectedProjectShowTasks ? (
                <div className="space-y-3">
                  <label className="block font-medium">{t("report.taskEntries")}</label>
                  {taskBundleLoading ? (
                    <div className="flex items-center gap-2 text-gray-500 text-sm py-2" role="status" aria-live="polite">
                      <svg className="animate-spin h-5 w-5 text-gray-400 flex-shrink-0" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" aria-hidden="true">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      <span>{t("report.loadingTasks")}</span>
                    </div>
                  ) : (
                    <TaskTimeEntryList
                      entries={combinedEntries}
                      onUpdateHours={handleCombinedUpdateHours}
                      onUpdateDescription={handleCombinedUpdateDescription}
                      onRemove={handleCombinedRemove}
                      onAddClick={() => setIsTaskPickerOpen(true)}
                      hideLabel
                      hideBillingModel
                      hideInputHourSuffix
                    />
                  )}
                </div>
              ) : null}

              {showHoursField ? (
                <div>
                  <label>{t("report.numberOfHours")}</label>
                  <input
                    ref={hoursRef}
                    type="text"
                    name="hours"
                    value={formik.values.hours}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    inputMode="decimal"
                    className="w-full px-3 py-2 border rounded"
                  />
                  {formik.touched.hours && formik.errors.hours ? (
                    <div className="text-red-500">{formik.errors.hours}</div>
                  ) : null}
                </div>
              ) : null}

              {/* Description field. Globe (translate) only in edit mode. */}
              <div className="relative" ref={descriptionTranslateRef}>
                <label className="flex items-center gap-2 flex-wrap">
                  <span>{t("report.descriptionRequired")}</span>
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 border border-gray-200">
                    {t("report.descriptionPublicHint")}
                  </span>
                </label>
                <p className="text-xs text-gray-500 mb-1">{t("report.descriptionPublicHelp")}</p>
                <div className="relative">
                  <textarea
                    ref={descriptionRef}
                    name="description"
                    value={formik.values.description}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className={`w-full px-3 py-2 border rounded ${reportToEdit ? "pr-10" : ""}`}
                    rows={3}
                    required={!isAbsent}
                  />
                  {reportToEdit && (
                    <button
                      type="button"
                      onClick={handleDescriptionTranslateClick}
                      className="absolute right-2 top-2 p-1.5 rounded text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-1 focus:ring-gray-400"
                      title={t("report.translateDescriptionTitle")}
                      aria-label={t("report.translateDescriptionAria")}
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 21a9.004 9.004 0 008.716-6.747M12 21a9.004 9.004 0 01-8.716-6.747M12 21c2.485 0 4.5-4.03 4.5-9S14.485 3 12 3m0 18c-2.485 0-4.5-4.03-4.5-9S9.515 3 12 3m0 0a8.997 8.997 0 017.843 4.582M12 3a8.997 8.997 0 00-7.843 4.582m15.686 0A11.953 11.953 0 0112 10.5c-2.998 0-5.74-1.1-7.843-2.918m15.686 0A8.959 8.959 0 0121 12c0 .778-.099 1.533-.284 2.253m0 0A17.919 17.919 0 0112 16.5c-3.162 0-6.133-.815-8.716-2.247m0 0A9.015 9.015 0 013 12c0-1.605.42-3.113 1.157-4.418" />
                      </svg>
                    </button>
                  )}
                </div>
                {reportToEdit && descriptionTranslateOpen && (
                  <div
                    className="absolute z-50 right-0 top-full mt-1 w-80 max-w-[calc(100vw-2rem)] rounded-lg border border-gray-200 bg-white shadow-lg p-3"
                    role="dialog"
                    aria-label={t("report.translationDialogAria")}
                  >
                    {descriptionTranslationLoading ? (
                      <p className="text-sm text-gray-500">{t("report.translating")}</p>
                    ) : descriptionTranslation !== null ? (
                      <>
                        {descriptionTranslationLang && (
                          <p className="text-xs text-gray-400 mb-1.5">{descriptionTranslationLang}</p>
                        )}
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{descriptionTranslation || t("report.noTranslationAvailable")}</p>
                      </>
                    ) : (formik.values.description || "").trim() ? (
                      <p className="text-sm text-red-600">{t("report.translationFailed")}</p>
                    ) : (
                      <p className="text-sm text-gray-500">{t("report.translationHint")}</p>
                    )}
                  </div>
                )}
                {formik.touched.description && formik.errors.description ? (
                  <div className="text-red-500">{formik.errors.description}</div>
                ) : null}
              </div>

              <div>
                <label>{t("report.uploadPhotos")}</label>
                <input
                  id="photo-upload"
                  type="file"
                  multiple
                  onChange={handlePhotoUpload}
                  className="block w-full"
                  accept={allowedPhotoTypes}
                  aria-label={t("report.uploadPhotos")}
                />
                {photos.length > 0 && (
                  <p className="text-sm text-gray-600 mt-1">
                    {t("report.photosReadyCount", { count: photos.length })}
                  </p>
                )}
                {!reportToEdit && photoRequirement.allTasksOptedOut && (
                  <p className="text-sm text-gray-600 mt-1">
                    {t("report.photosOptionalForTasks")}
                  </p>
                )}
                {!reportToEdit && photoRequirement.requiredCount > 0 && (
                  <p className={`text-sm mt-1 ${photosShort ? "text-red-600" : "text-gray-600"}`}>
                    {t("report.photosRequiredHint", {
                      hours: formatPhotoHours(photoRequirement.photoRequiredHours),
                      count: photoRequirement.requiredCount,
                    })}
                  </p>
                )}
              </div>

              <ReportFlagChips
                drafts={flagDrafts}
                onOpen={(type) => setFlagSheet({ type, initialDraft: null })}
                internalNote={internalNote}
                onOpenInternalNote={() => setInternalNoteSheetOpen(true)}
              />
            </>
          ) : (
            <div>
              <label className="block mb-3 font-medium">{t("report.reasonForAbsence")}</label>
              <div className="space-y-2">
                {[
                  {
                    value: "Flex day",
                    id: "F",
                    labelKey: "report.absence.flexDay",
                    descriptionKey: "report.absence.flexDayDesc",
                  },
                  {
                    value: "Holiday",
                    id: "H",
                    labelKey: "report.absence.holiday",
                    descriptionKey: "report.absence.holidayDesc",
                  },
                  {
                    value: "Illness",
                    id: "I",
                    labelKey: "report.absence.illness",
                    descriptionKey: "report.absence.illnessDesc",
                  },
                  {
                    value: "No work available",
                    id: "N",
                    labelKey: "report.absence.noWorkAvailable",
                    descriptionKey: "report.absence.noWorkAvailableDesc",
                  },
                  {
                    value: "Other",
                    id: "O",
                    labelKey: "report.absence.other",
                    descriptionKey: null,
                  },
                ].map((option) => (
                  <label
                    key={option.value}
                    className={`flex items-start p-4 border-2 rounded-lg cursor-pointer transition-all hover:bg-gray-50 ${
                      absenceReason === option.value
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200"
                    }`}
                  >
                    <input
                      type="radio"
                      name="absenceReason"
                      value={option.value}
                      checked={absenceReason === option.value}
                      onChange={(e) => {
                        setAbsenceReason(e.target.value);
                        if (e.target.value !== "Flex day") {
                          setFlexDayRepeatCount(0);
                        }
                        if (e.target.value !== "Holiday") {
                          setHolidayBackToWorkDate("");
                        }
                        if (e.target.value !== "Other") {
                          setOtherDescription("");
                        }
                      }}
                      className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="ml-3 flex-1 flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">{t(option.labelKey)}</div>
                        {option.descriptionKey && (
                          <div className="text-sm text-gray-600 mt-1">{t(option.descriptionKey)}</div>
                        )}
                      </div>
                      {/* Inline repeat section for Flex Day */}
                      {option.value === "Flex day" && absenceReason === "Flex day" && (
                        <div className="w-[39%] ml-4 pl-4 border-l border-gray-300">
                          <label
                            htmlFor="flexDayRepeatCount"
                            className="block text-xs font-medium text-gray-700 mb-1"
                          >
                            {t("report.repeatFlexDay", { count: flexDayRepeatCount, weeks: flexDayRepeatCount === 1 ? t("report.week") : t("report.weeks") })}
                          </label>
                          <input
                            id="flexDayRepeatCount"
                            type="number"
                            inputMode="numeric"
                            min="0"
                            max="52"
                            value={flexDayRepeatCount}
                            onChange={(e) => {
                              const value = parseInt(e.target.value) || 0;
                              setFlexDayRepeatCount(Math.max(0, Math.min(52, value)));
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        </div>
                      )}
                      {/* Inline date picker section for Holiday */}
                      {option.value === "Holiday" && absenceReason === "Holiday" && (
                        <div className="w-[39%] ml-4 pl-4 border-l border-gray-300">
                          <label
                            htmlFor="holidayBackToWorkDate"
                            className="block text-xs font-medium text-gray-700 mb-1"
                          >
                            {t("report.backToWork")}
                          </label>
                          <input
                            id="holidayBackToWorkDate"
                            type="date"
                            value={holidayBackToWorkDate}
                            min={formik.values.date ? new Date(new Date(formik.values.date).getTime() + 86400000).toISOString().split("T")[0] : ""}
                            onChange={(e) => {
                              setHolidayBackToWorkDate(e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                          {holidayBackToWorkDate && formik.values.date && new Date(holidayBackToWorkDate) <= new Date(formik.values.date) && (
                            <div className="text-xs text-red-500 mt-1">
                              {t("report.backToWorkDateAfterReport")}
                            </div>
                          )}
                        </div>
                      )}
                      {/* Inline description textarea section for Other */}
                      {option.value === "Other" && absenceReason === "Other" && (
                        <div className="w-[75%] ml-4 pl-4 border-l border-gray-300">
                          <label
                            htmlFor="otherDescription"
                            className="block text-xs font-medium text-gray-700 mb-1"
                          >
                            {t("report.otherDescriptionLabel")} <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            ref={otherDescriptionRef}
                            id="otherDescription"
                            value={otherDescription}
                            onChange={(e) => {
                              setOtherDescription(e.target.value);
                            }}
                            onClick={(e) => e.stopPropagation()}
                            onFocus={(e) => e.stopPropagation()}
                            required
                            rows={3}
                            className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                            placeholder={t("report.otherDescriptionPlaceholder")}
                          />
                        </div>
                      )}
                    </div>
                  </label>
                ))}
              </div>
            </div>
          )}

          <button
            type="submit"
            className="w-full px-3 py-2 bg-blue-500 text-white rounded disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={loading || isProcessingPhotos || photosShort}
          >
            {isProcessingPhotos
              ? t("report.processingPhotos")
              : reportToEdit
              ? t("report.updateReport")
              : t("report.submitReport")}
          </button>
        </form>

        {/* Loading overlay while the report itself is saving */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center space-y-3 max-w-sm mx-4 w-full">
              <svg
                className="animate-spin h-8 w-8 text-blue-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              <p className="text-lg font-medium text-gray-800">
                {reportToEdit ? t("report.updatingReport") : t("report.savingReport")}
              </p>
              <p className="text-sm text-gray-600 text-center">
                {t("report.pleaseWaitSaving")}
              </p>
            </div>
          </div>
        )}

        {/* Success overlay */}
        {successMessage && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center space-y-3 max-w-sm mx-4 relative">
              <button
                onClick={() => setSuccessMessage("")}
                className="absolute top-2 right-2 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label={t("report.close")}
              >
                <svg
                  className="h-5 w-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <div className="h-12 w-12 bg-green-100 rounded-full flex items-center justify-center">
                <svg
                  className="h-6 w-6 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
              <p className="text-lg font-medium text-gray-800">
                {successMessage}
              </p>
              <p className="text-sm text-gray-600 text-center">
                {t("report.savedSuccessfully")}
              </p>
              {flagsSentMessage && (
                <p className="text-sm text-blue-700 text-center">{flagsSentMessage}</p>
              )}
            </div>
          </div>
        )}

        {/* Photo processing overlay */}
        {isProcessingPhotos && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-xl flex flex-col items-center space-y-3 max-w-sm mx-4">
              <svg
                className="animate-spin h-8 w-8 text-blue-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                ></path>
              </svg>
              <p className="text-lg font-medium text-gray-800">{t("report.processingPhotosOverlay")}</p>
              <p className="text-sm text-gray-600 text-center">
                {t("report.optimizingImages")}
              </p>
            </div>
          </div>
        )}

        {/* Task Picker Modal */}
        <TaskPickerModal
          isOpen={isTaskPickerOpen}
          onClose={() => setIsTaskPickerOpen(false)}
          onSelect={handleAddTaskEntry}
          bundle={taskBundle}
          recentTasks={recentTasks}
          onBundleUpdated={taskBundle ? (newBundle) => setTaskBundle(newBundle) : undefined}
          onReportMissingTask={handleReportMissingTask}
        />

        <InternalNoteSheet
          isOpen={internalNoteSheetOpen}
          value={internalNote}
          onSave={setInternalNote}
          onClose={() => setInternalNoteSheetOpen(false)}
        />

        <ReportFlagSheet
          isOpen={!!flagSheet}
          type={flagSheet?.type}
          initialDraft={flagSheet?.initialDraft}
          drafts={flagDrafts}
          taskEntries={taskEntries}
          onSave={handleSaveFlagDraft}
          onRemove={handleRemoveFlagDraft}
          onClose={() => setFlagSheet(null)}
        />

      </PageWrapper>
    </>
  );
}

export default Report;
